import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from "react-router-dom";
import { BrowserRouter, Switch, Route, useHistory, Redirect } from "react-router-dom";
import Amplify, { Auth, API } from "aws-amplify";
import swal from 'sweetalert';

import LogIn from './LogIn';
import LoadPage from './LoadPage';
import Loading from './Loading';
import Navbar from './Navbar';
import Settings from './Settings';
import Documentation from './Documentation';
import NewYearModal from './NewYearModal';
import SwitchingYearsModal from './SwitchingYearsModal';
import UniversalSidebar from './UniversalSidebar';

import SelectSchool from './SelectSchool';
import Progress from './Progress';
import SchoolData from './SchoolData';
import SchedulingData from './SchedulingData';
import Magnetboard from './Magnetboard';
import HSMSReportsHost from './hsms/reports/HSMSReportsHost';
import useMagnetboardStore from './hsms/store/magnetboardStore';
import { logMagnetboardDiagnostic } from './hsms/magnetboard/utils/diagnostics';

import { getData, loadData, getJSONFromStorage, getNotifications, switchCurrentYear, JSONexists, isEmpty, calculateStudentRequestStats, calculateStudentMatchPercent, calculateStudentScheduleFullness } from '../js/Helpers';
import { HSMS_SHARED_DATA_INVALIDATED_EVENT, normalizeHSMSSlices } from '../js/HSMSDataSync';
import { getNavigationGuardRedirect, navigationGuardIsPending } from '../js/navigtionGuard';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faIdCard, faCog, faQuestion, faWandMagicSparkles, faCalendarDays, faNetworkWired } from '@fortawesome/free-solid-svg-icons';

import '../css/common.css';

const host_name = (typeof window !== 'undefined' && window.location && window.location.hostname) ? window.location.hostname : '';
const running_on_localhost = host_name === 'localhost' || host_name === '127.0.0.1';
const cookie_domain = (process.env.REACT_APP_COOKIE_DOMAIN || '').trim();
const schoolConnectionIsActive = (school_info) => `${school_info?.is_active ?? '1'}` !== '0';

function ProtectedRoute({ routeType, guardInfo, render, ...routeProps }) {
	return (
		<Route {...routeProps} render={(props) => {
			const guard_input = {
				...guardInfo,
				routeType,
				params: props.match.params,
				currentPathname: props.location.pathname,
			};
			if(navigationGuardIsPending(guard_input)) return null;

			const redirect_path = getNavigationGuardRedirect({
				...guard_input,
			});

			if(redirect_path) return <Redirect to={redirect_path} />;
			return render(props);
		}} />
	);
}

function SupervisorNoDepartmentsModal() {
	return (
		<div className='supervisor-no-departments-modal' role='dialog' aria-modal='true' aria-labelledby='supervisor-no-departments-title'>
			<div className='supervisor-no-departments-logo-container'>
				<img className='supervisor-no-departments-logo-img' src={require("../images/logo-dark.png")} alt='Edario Logo' />
				<div className='supervisor-no-departments-logo-text'>edario</div>
			</div>
			<div className='supervisor-no-departments-card'>
				<img className='supervisor-no-departments-illustration' src={require("../images/supervisor-no-departments.png")} alt='' aria-hidden='true' />
				<h1 id='supervisor-no-departments-title'>No departments assigned</h1>
				<p>You have not been assigned any departments yet. Please contact your scheduling admin to give you access.</p>
			</div>
		</div>
	);
}

function SupervisorDepartmentAccessLoading() {
	return (
		<div className='supervisor-department-access-loading'>
			<Loading/>
		</div>
	);
}

const normalizeIDArray = (values = []) => (
	Array.isArray(values)
		? [...new Set(values.map(value => `${value}`).filter(value => value && value !== 'undefined' && value !== 'null'))]
		: []
);

const mergeRecordsByID = (current_records, incoming_records, id_key, deleted_ids = []) => {
	const deleted_id_set = new Set(normalizeIDArray(deleted_ids));
	const incoming_records_by_id = (Array.isArray(incoming_records) ? incoming_records : []).reduce((results, record_info) => {
		const record_id = record_info && record_info[id_key] !== undefined && record_info[id_key] !== null ? `${record_info[id_key]}` : null;
		if(record_id) results[record_id] = record_info;
		return results;
	}, {});

	const merged_records = (Array.isArray(current_records) ? current_records : []).reduce((results, record_info) => {
		const record_id = record_info && record_info[id_key] !== undefined && record_info[id_key] !== null ? `${record_info[id_key]}` : null;
		if(record_id && deleted_id_set.has(record_id)) return results;
		if(record_id && incoming_records_by_id[record_id]) {
			results.push(incoming_records_by_id[record_id]);
			delete incoming_records_by_id[record_id];
			return results;
		}
		results.push(record_info);
		return results;
	}, []);

	return [...merged_records, ...Object.values(incoming_records_by_id)];
};

const sortCoursesByName = (course_records = []) => (
	[...course_records].sort((course_a, course_b) => {
		const course_name_a = `${course_a?.name || ''}`;
		const course_name_b = `${course_b?.name || ''}`;
		const name_comparison = course_name_a.localeCompare(course_name_b);
		if(name_comparison !== 0) return name_comparison;

		return `${course_a?.course_code || ''}`.localeCompare(`${course_b?.course_code || ''}`);
	})
);

const mergeSubsectionRecords = (current_records, incoming_records, deleted_ids = []) => {
	const deleted_id_set = new Set(normalizeIDArray(deleted_ids));
	const filtered_records = (Array.isArray(current_records) ? current_records : []).filter(record_info => {
		const section_id = record_info?.section_id !== undefined && record_info?.section_id !== null ? `${record_info.section_id}` : null;
		const subsection_id = record_info?.subsection_id !== undefined && record_info?.subsection_id !== null ? `${record_info.subsection_id}` : null;
		return !(section_id && deleted_id_set.has(section_id)) && !(subsection_id && deleted_id_set.has(subsection_id));
	});

	return mergeRecordsByID(filtered_records, incoming_records, 'subsection_id');
};

const amplify_auth_config = {
	region: process.env.REACT_APP_AWS_REGION,
	userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
	userPoolWebClientId: process.env.REACT_APP_COGNITO_APP_CLIENT_ID,
};

// For local localhost development, let Amplify use its default storage.
// A fixed cookie domain (for example ".edario.local") prevents Cognito
// session cookies from being persisted on localhost.
if(cookie_domain && !running_on_localhost)
{
	amplify_auth_config.cookieStorage = {
		domain: cookie_domain,
		path: '/',
		expires: 365,
		secure: false
	};
}

Amplify.configure({
	...amplify_auth_config,
	API: {
		endpoints: [
			{
				name: process.env.REACT_APP_ENDPOINT_NAME,
				endpoint: process.env.REACT_APP_ENDPOINT,
				custom_header: async function() {
					return { Authorization: `Bearer ${(await Auth.currentSession()).getAccessToken().getJwtToken()}` };
				}
			},
			{
				name: process.env.REACT_APP_ELEM_ENDPOINT_NAME,
				endpoint: process.env.REACT_APP_ELEM_ENDPOINT,
				custom_header: async function() {
					return { Authorization: `Bearer ${(await Auth.currentSession()).getAccessToken().getJwtToken()}` };
				}
			},
			{
				name: process.env.REACT_APP_ENDPOINT_NAME_V2,
				endpoint: process.env.REACT_APP_ENDPOINT_V2,
				custom_header: async function() {
					return { Authorization: `Bearer ${(await Auth.currentSession()).getAccessToken().getJwtToken()}` };
				}
			}
		]
	}
});

export default function SchedulingApp({match}) {
	let history = useHistory();

	const isUnderMaintenance = useRef(false);
	const pendingHSMSDataRefreshes = useRef(new Map());
	const [isAuth, setIsAuth] = useState(false);
	const [showNewYearModal, setShowNewYearModal] = useState(false);
	const [showSwitchingYearModal, setShowSwitchingYearModal] = useState(false);
	
	const [username, setUsername] = useState(null);
	const [password, setPassword] = useState(null);
	const [currentUser, setCurrentUser] = useState(null);
	const [currentSchoolID, setCurrentSchoolID] = useState(null);
	const [authComplete, setAuthComplete] = useState(false);
	const [newPassword, setNewPassword] = useState(null);
	const [verificationCode, setVerificationCode] = useState(null);
		
	const [userInfo, setUserInfo] = useState({});
	const [schoolInfo, setSchoolInfo] = useState({});
	const [scheduleInfo, setScheduleInfo] = useState({});
	const [scheduleStructureData, setScheduleStructureData] = useState({});
	const [users, setUsers] = useState([]);
	const [schoolSubscribed, setSchoolSubscribed] = useState(false);
	
	const [rolePages, setRolePages] = useState(null);
	const [appSubscreens, setAppSubscreens] = useState(null);
	const [notifications, setNotifications] = useState(null);

	const [schoolType, setSchoolType] = useState(null);
	const [selectedScheduleYearID, setSelectedScheduleYearID] = useState(null);
	const [loadScreen, setLoadScreen] = useState(null);

	const [departments, setDepartments] = useState([]);
	const [teachers, setTeachers] = useState([]);
	const [courses, setCourses] = useState([]);
	const [classrooms, setClassrooms] = useState([]);
	const [students, setStudents] = useState([]);
	const [studentMatchPercentages, setStudentMatchPercentages] = useState({});
	const [sections, setSections] = useState([]);
	const [subsections, setSubsections] = useState([]);
	const [periods, setPeriods] = useState([]);
	const [blocks, setBlocks] = useState([]);
	const [lunchInfo, setLunchInfo] = useState({});
	const [labels, setLabels] = useState([]);
	const [pulloutGroups, setPulloutGroups] = useState([]);
	const [homeroomGradeGroups, setHomeroomGradeGroups] = useState([]);
	const [homeroomGradeGroupsLoading, setHomeroomGradeGroupsLoading] = useState(true);

	const [screenAllowsUniversalSidebar, setScreenAllowsUniversalSidebar] = useState(false);
	const [showUniversalSidebar, setShowUniversalSidebar] = useState(false);
	const [universalSidebarClosing, setUniversalSidebarClosing] = useState(false);
	const [universalSidebarLoadingInfo, setUniversalSidebarLoadingInfo] = useState(null);
	
	/////////////////////////////
	///// LOADING VARIABLES /////
	/////////////////////////////
	const [schoolProgressLoaded, setSchoolProgressLoaded] = useState(false);
	const [userInfoLoading, setUserInfoLoading] = useState(true);
	const [schoolInfoLoading, setSchoolInfoLoading] = useState(true);
	const [scheduleStructureDataLoading, setScheduleStructureDataLoading] = useState(true);
	const [periodsLoading, setPeriodsLoading] = useState(true);
	const [scheduleInfoLoading, setScheduleInfoLoading] = useState(true);
	const [usersLoading, setUsersLoading] = useState(true);
	const [departmentsLoading, setDepartmentsLoading] = useState(true);
	const [coursesLoading, setCoursesLoading] = useState(true);
	const [teachersLoading, setTeachersLoading] = useState(true);
	const [classroomsLoading, setClassroomsLoading] = useState(true);
	const [studentsLoading, setStudentsLoading] = useState(true);
	const [sectionsLoading, setSectionsLoading] = useState(true);
	const [blocksLoading, setBlocksLoading] = useState(true);
	const [, setPulloutGroupsLoading] = useState(true);
	const [lunchInfoLoading, setLunchInfoLoading] = useState(true);
	
	const handleUsernameChange = (e) => {
		setUsername(e.target.value.trim());
	}
	
	const handlePasswordChange = (e) => {
		setPassword(e.target.value.trim());
	}
	
	const handleNewPasswordChange = (e) => {
		setNewPassword(e.target.value.trim());
	}
	
	const handleVerificationCodeChange = (e) => {
		setVerificationCode(e.target.value.trim());
	}
	
	const handleLogout = () => {
		Auth.signOut().then(() => {
			setUsername(null);
			setIsAuth(null);
			setLoadScreen('login');

			resetState();
		}).catch();
	}
	
	const handleLogin = () => {
		const promise = new Promise((resolve, reject) => {
			Auth.signIn(username, password).then(user => {
				if(user.challengeName === 'NEW_PASSWORD_REQUIRED')
				{
					setCurrentUser(user);
					resolve(user);
				}
				else
				{
					setCurrentUser(user);
					setIsAuth(true);
					setPassword(null);
					setNewPassword(null);
					setVerificationCode(null);
					
					resolve(user);
				}
			}).catch(error => {
				//setLoadScreen('login');
				reject(error);
			}).finally(() => {
				setAuthComplete(true);
			});
		});
		
		return promise;
	}
	
	const handleCompleteNewPassword = () => {
		const promise = new Promise((resolve, reject) => {
			
			Auth.completeNewPassword(currentUser, newPassword).then(user => {
	            setCurrentUser(user);
	            setIsAuth(true);
				setPassword(null);
				setNewPassword(null);
				setVerificationCode(null);
				
				resolve(user);
	        }).catch(error => {
	         	reject(error);
	        }).finally(() => {
				setAuthComplete(true);
			});
		});
		
		return promise;
	}
	
	const handleForgotPassword = () => {
		const promise = new Promise((resolve, reject) => {
			Auth.forgotPassword(username).then(data => {
				setNewPassword(null);
				setVerificationCode(null);
				
				resolve(true);
	        }).catch(error => {
	         	reject(error);
	        }).finally(() => {
				setAuthComplete(true);
			});
		});
		
		return promise;
	}
	
	const handleForgotPasswordSubmit = () => {
		const promise = new Promise((resolve, reject) => {
			
			Auth.forgotPasswordSubmit(username, verificationCode, newPassword).then(data => {
	            setIsAuth(true);
				setUsername(null);
				setPassword(null);
				setNewPassword(null);
				setVerificationCode(null);
				
				resolve(true);
	        }).catch(error => {
	         	reject(error);
	        }).finally(() => {
				setAuthComplete(true);
			});
		});
		
		return promise;
	}

	const getUniqueSchoolsById = useCallback((schools = []) => {
		const logged_school_ids = new Set();

		return schools.reduce((results, school_info) => {
			const school_id = school_info && school_info.school_id;
			if(!school_id || logged_school_ids.has(school_id)) return results;

			logged_school_ids.add(school_id);
			results.push(school_info);
			return results;
		}, []);
	}, []);
	
	const authUser = async () => {
		try {
			const user = await Auth.currentAuthenticatedUser();
			setCurrentUser(user);
			setIsAuth(true);
			setAuthComplete(true);
		} catch {
			setCurrentUser(null);
			setIsAuth(false);
			setAuthComplete(true);
			setLoadScreen('login');
		}	
	}
	
	const resetState = useCallback(() => {
		useMagnetboardStore.getState().resetStore();

		localStorage.setItem('currentSchoolID', null);

		setCurrentSchoolID(null);
		setSchoolInfo({});
		setScheduleInfo({});
		setScheduleStructureData({});
		setPeriods([]);
		setUsers([]);
		setRolePages(null);
		setAppSubscreens(null);
		setNotifications(null);
		setSchoolType(null);

		setDepartments([]);
		setTeachers([]);
		setCourses([]);
		setClassrooms([]);
		setStudents([]);
		setStudentMatchPercentages({});
		setSections([]);
		setSubsections([]);
		setPeriods([]);
		setBlocks([]);
		setLunchInfo({});
		setLabels([]);
		setPulloutGroups([]);
		setHomeroomGradeGroups([]);
		setHomeroomGradeGroupsLoading(true);

		setSchoolProgressLoaded(false);
		setSchoolInfoLoading(true);
		setScheduleStructureDataLoading(true);
		setPeriodsLoading(true);
		setScheduleInfoLoading(true);
		setUsersLoading(true);
		setDepartmentsLoading(true);
		setCoursesLoading(true);
		setTeachersLoading(true);
		setClassroomsLoading(true);
		setStudentsLoading(true);
		setSectionsLoading(true);
		setBlocksLoading(true);
		setPulloutGroupsLoading(true);
		setLunchInfoLoading(true);
	},[])

	const handlePullNewData = (passed_credentials = {}) => {
		const school_id = (passed_credentials && 'school_id' in passed_credentials) ? passed_credentials.school_id : schoolInfo.school_id;
		const schedule_version_id = (passed_credentials && 'schedule_version_id' in passed_credentials) ? passed_credentials.schedule_version_id : schoolInfo.current_schedule_version_id;
		const data_version_id = (passed_credentials && 'data_version_id' in passed_credentials) ? passed_credentials.data_version_id : schoolInfo.current_data_version_id;
		const database = (passed_credentials && 'database' in passed_credentials) ? passed_credentials.database : ((schoolInfo.type === '1') ? 'edario' : 'elementary');

		const credentials = {school_id:school_id, schedule_version_id:schedule_version_id, data_version_id:data_version_id, database:database};

		loadData({data_types:['students', 'courses', 'teachers', 'classrooms', 'pullout_groups'], ...credentials}).then(database_data => {
			setStudents([...(Array.isArray(database_data?.students) ? database_data.students : [])]);
			setCourses([...(Array.isArray(database_data?.courses) ? database_data.courses : [])]);
			setTeachers([...(Array.isArray(database_data?.teachers) ? database_data.teachers : [])]);
			setClassrooms([...(Array.isArray(database_data?.classrooms) ? database_data.classrooms : [])]);
			setPulloutGroups([...(Array.isArray(database_data?.pullout_groups) ? database_data.pullout_groups : [])]);

			setStudentsLoading(false);
			setCoursesLoading(false);
			setTeachersLoading(false);
			setClassroomsLoading(false);
			setPulloutGroupsLoading(false);
		});
		
		if(database === 'edario')
		{

			loadData({data_types:['departments'], ...credentials}).then(database_data => {
				setDepartments([...(Array.isArray(database_data?.departments) ? database_data.departments : [])]);
				setDepartmentsLoading(false);
			});

			getData('sections', '/get-sections', credentials, true).then(returned_data => {
				setSections([...(Array.isArray(returned_data?.sections) ? returned_data.sections : [])]);
				setSubsections([...(Array.isArray(returned_data?.subsections) ? returned_data.subsections : [])]);
				setLabels([...(Array.isArray(returned_data?.labels) ? returned_data.labels : [])]);
				setSectionsLoading(false);
			});
	
			getData('lunchInfo', '/get-lunch-info', credentials, true).then(lunch_info_data => {
				setLunchInfo((lunch_info_data && typeof lunch_info_data === 'object') ? lunch_info_data : {});
				setLunchInfoLoading(false);
			});
		}
		else if(database === 'elementary')
		{
			getData('blocks', '/elementary/get-blocks', {school_id:school_id, schedule_version_id:schedule_version_id}, true, 'elementary').then(returned_data => {
				setBlocks([...(Array.isArray(returned_data) ? returned_data : [])]);
				setBlocksLoading(false);
			});

			setHomeroomGradeGroupsLoading(true);
			getData('homeroomGradeGroups', '/elementary/get-homeroom-grades', {school_id:school_id, schedule_version_id:schedule_version_id}, true, 'elementary').then(returned_data => {
				const groups = (Array.isArray(returned_data)) ? returned_data : [];
				setHomeroomGradeGroups(groups);
			}).catch(() => {
				setHomeroomGradeGroups([]);
			}).finally(() => {
				setHomeroomGradeGroupsLoading(false);
			});
		}
	};

	const refreshHSMSDataSlices = useCallback(async ({
		slices = [],
		passed_credentials = {},
		targets = null,
	} = {}) => {
		if(schoolType !== 'hsms') return;

		const normalized_slices = normalizeHSMSSlices(slices);
		if(normalized_slices.length === 0) return;

		const school_id = ('school_id' in passed_credentials) ? passed_credentials.school_id : schoolInfo.school_id;
		const schedule_version_id = ('schedule_version_id' in passed_credentials) ? passed_credentials.schedule_version_id : schoolInfo.current_schedule_version_id;
		const data_version_id = ('data_version_id' in passed_credentials) ? passed_credentials.data_version_id : schoolInfo.current_data_version_id;
		const schedule_year_id = ('schedule_year_id' in passed_credentials) ? passed_credentials.schedule_year_id : schoolInfo.current_schedule_year_id;

		if(!school_id || !schedule_version_id) return;

		const credentials = {school_id, schedule_version_id, data_version_id, database:'edario'};
		const requested_slices = new Set(normalized_slices);
		const queueRefresh = (refresh_key, refresh_callback) => {
			const pending_key = `${school_id}:${schedule_version_id}:${data_version_id || ''}:${refresh_key}`;
			if(pendingHSMSDataRefreshes.current.has(pending_key)) return pendingHSMSDataRefreshes.current.get(pending_key);

			const refresh_promise = Promise.resolve()
				.then(refresh_callback)
				.finally(() => {
					pendingHSMSDataRefreshes.current.delete(pending_key);
				});

			pendingHSMSDataRefreshes.current.set(pending_key, refresh_promise);
			return refresh_promise;
		};

		const targeted_slices = new Set();
		const targeted_refresh_targets = (targets && ['course_save', 'student_save', 'teacher_save', 'classroom_save', 'section_save'].includes(targets.type)) ? targets : null;

		if(targeted_refresh_targets)
		{
			const course_ids = normalizeIDArray(targeted_refresh_targets?.courses?.ids);
			const deleted_course_ids = normalizeIDArray(targeted_refresh_targets?.courses?.deleted_ids);
			const student_ids = normalizeIDArray(targeted_refresh_targets?.students?.ids);
			const deleted_student_ids = normalizeIDArray(targeted_refresh_targets?.students?.deleted_ids);
			const teacher_ids = normalizeIDArray(targeted_refresh_targets?.teachers?.ids);
			const deleted_teacher_ids = normalizeIDArray(targeted_refresh_targets?.teachers?.deleted_ids);
			const classroom_ids = normalizeIDArray(targeted_refresh_targets?.classrooms?.ids);
			const deleted_classroom_ids = normalizeIDArray(targeted_refresh_targets?.classrooms?.deleted_ids);
			const section_ids = normalizeIDArray(targeted_refresh_targets?.sections?.ids);
			const deleted_section_ids = normalizeIDArray(targeted_refresh_targets?.sections?.deleted_ids);
			const section_course_ids = normalizeIDArray(targeted_refresh_targets?.sections?.course_ids);
			const targeted_refresh_tasks = [];
			const targeted_refresh_slices = new Set();

			if(requested_slices.has('courses') && (course_ids.length > 0 || deleted_course_ids.length > 0))
			{
				targeted_refresh_tasks.push(queueRefresh(`${targeted_refresh_targets.type}:courses:${[...course_ids, ...deleted_course_ids].sort().join(',')}`, async () => {
					const database_data = course_ids.length > 0
						? await loadData({
							data_types:['courses'],
							target_ids: {courses: {ids: course_ids}},
							...credentials
						})
						: {courses: []};
					setCourses(current_courses => sortCoursesByName(mergeRecordsByID(current_courses, database_data?.courses || [], 'course_id', deleted_course_ids)));
				}));
				targeted_refresh_slices.add('courses');
			}

			if(requested_slices.has('students') && (student_ids.length > 0 || deleted_student_ids.length > 0))
			{
				targeted_refresh_tasks.push(queueRefresh(`${targeted_refresh_targets.type}:students:${[...student_ids, ...deleted_student_ids].sort().join(',')}`, async () => {
					const database_data = student_ids.length > 0
						? await loadData({
							data_types:['students'],
							target_ids: {students: {ids: student_ids}},
							...credentials
						})
						: {students: []};
					setStudents(current_students => mergeRecordsByID(current_students, database_data?.students || [], 'student_id', deleted_student_ids));
				}));
				targeted_refresh_slices.add('students');
			}

			if(requested_slices.has('teachers') && (teacher_ids.length > 0 || deleted_teacher_ids.length > 0))
			{
				targeted_refresh_tasks.push(queueRefresh(`${targeted_refresh_targets.type}:teachers:${[...teacher_ids, ...deleted_teacher_ids].sort().join(',')}`, async () => {
					const database_data = teacher_ids.length > 0
						? await loadData({
							data_types:['teachers'],
							target_ids: {teachers: {ids: teacher_ids}},
							...credentials
						})
						: {teachers: []};
					setTeachers(current_teachers => mergeRecordsByID(current_teachers, database_data?.teachers || [], 'teacher_id', deleted_teacher_ids));
				}));
				targeted_refresh_slices.add('teachers');
			}

			if(requested_slices.has('classrooms') && (classroom_ids.length > 0 || deleted_classroom_ids.length > 0))
			{
				targeted_refresh_tasks.push(queueRefresh(`${targeted_refresh_targets.type}:classrooms:${[...classroom_ids, ...deleted_classroom_ids].sort().join(',')}`, async () => {
					const database_data = classroom_ids.length > 0
						? await loadData({
							data_types:['classrooms'],
							target_ids: {classrooms: {ids: classroom_ids}},
							...credentials
						})
						: {classrooms: []};
					setClassrooms(current_classrooms => mergeRecordsByID(current_classrooms, database_data?.classrooms || [], 'classroom_id', deleted_classroom_ids));
				}));
				targeted_refresh_slices.add('classrooms');
			}

			if((requested_slices.has('sections') || requested_slices.has('subsections')) && (section_ids.length > 0 || section_course_ids.length > 0 || deleted_section_ids.length > 0))
			{
				targeted_refresh_tasks.push(queueRefresh(`${targeted_refresh_targets.type}:sections:${[...section_ids, ...deleted_section_ids].sort().join(',')}:${[...section_course_ids].sort().join(',')}`, async () => {
					const returned_data = (section_ids.length > 0 || section_course_ids.length > 0)
						? await getData('sections', '/get-sections', {
							...credentials,
							target_ids: {
								sections: {
									ids: section_ids,
									course_ids: section_course_ids,
								}
							},
							partial_refresh: true,
						}, true)
						: {sections: [], subsections: []};
					setSections(current_sections => mergeRecordsByID(current_sections, returned_data?.sections || [], 'section_id', deleted_section_ids));
					setSubsections(current_subsections => mergeSubsectionRecords(current_subsections, returned_data?.subsections || [], deleted_section_ids));
				}));
				targeted_refresh_slices.add('sections');
				targeted_refresh_slices.add('subsections');
			}

			if(requested_slices.has('students') && student_ids.length === 0 && deleted_student_ids.length === 0) targeted_refresh_slices.add('students');
			if(requested_slices.has('teachers') && teacher_ids.length === 0 && deleted_teacher_ids.length === 0) targeted_refresh_slices.add('teachers');
			if(requested_slices.has('classrooms') && classroom_ids.length === 0 && deleted_classroom_ids.length === 0) targeted_refresh_slices.add('classrooms');
			if(requested_slices.has('sections') && section_ids.length === 0 && section_course_ids.length === 0 && deleted_section_ids.length === 0) targeted_refresh_slices.add('sections');
			if(requested_slices.has('subsections') && section_ids.length === 0 && section_course_ids.length === 0 && deleted_section_ids.length === 0) targeted_refresh_slices.add('subsections');

			if(targeted_refresh_tasks.length > 0)
			{
				try {
					await Promise.all(targeted_refresh_tasks);
					targeted_refresh_slices.forEach(slice => targeted_slices.add(slice));
				} catch(error) {
					console.warn('Targeted HSMS refresh failed; falling back to full slice refresh.', error);
				}
			}
			else
			{
				targeted_refresh_slices.forEach(slice => targeted_slices.add(slice));
			}
		}

		const refresh_tasks = [];

		if(requested_slices.has('students') && !targeted_slices.has('students'))
		{
			refresh_tasks.push(queueRefresh('students', async () => {
				const database_data = await loadData({data_types:['students'], ...credentials});
				setStudents([...(Array.isArray(database_data?.students) ? database_data.students : [])]);
			}));
		}

		if(requested_slices.has('courses') && !targeted_slices.has('courses'))
		{
			refresh_tasks.push(queueRefresh('courses', async () => {
				const database_data = await loadData({data_types:['courses'], ...credentials});
				setCourses([...(Array.isArray(database_data?.courses) ? database_data.courses : [])]);
			}));
		}

		if(requested_slices.has('teachers') && !targeted_slices.has('teachers'))
		{
			refresh_tasks.push(queueRefresh('teachers', async () => {
				const database_data = await loadData({data_types:['teachers'], ...credentials});
				setTeachers([...(Array.isArray(database_data?.teachers) ? database_data.teachers : [])]);
			}));
		}

		if(requested_slices.has('classrooms') && !targeted_slices.has('classrooms'))
		{
			refresh_tasks.push(queueRefresh('classrooms', async () => {
				const database_data = await loadData({data_types:['classrooms'], ...credentials});
				setClassrooms([...(Array.isArray(database_data?.classrooms) ? database_data.classrooms : [])]);
			}));
		}

		if(requested_slices.has('pullout_groups'))
		{
			refresh_tasks.push(queueRefresh('pullout_groups', async () => {
				const database_data = await loadData({data_types:['pullout_groups'], ...credentials});
				setPulloutGroups([...(Array.isArray(database_data?.pullout_groups) ? database_data.pullout_groups : [])]);
			}));
		}

		if(requested_slices.has('departments'))
		{
			refresh_tasks.push(queueRefresh('departments', async () => {
				const database_data = await loadData({data_types:['departments'], ...credentials});
				setDepartments([...(Array.isArray(database_data?.departments) ? database_data.departments : [])]);
			}));
		}

		const needs_sections_bundle_refresh =
			(requested_slices.has('sections') && !targeted_slices.has('sections')) ||
			(requested_slices.has('subsections') && !targeted_slices.has('subsections')) ||
			requested_slices.has('labels');

		if(needs_sections_bundle_refresh)
		{
			refresh_tasks.push(queueRefresh('sections_bundle', async () => {
				const returned_data = await getData('sections', '/get-sections', credentials, true);
				setSections([...(Array.isArray(returned_data?.sections) ? returned_data.sections : [])]);
				setSubsections([...(Array.isArray(returned_data?.subsections) ? returned_data.subsections : [])]);
				setLabels([...(Array.isArray(returned_data?.labels) ? returned_data.labels : [])]);
			}));
		}

		if(requested_slices.has('lunchInfo'))
		{
			refresh_tasks.push(queueRefresh('lunchInfo', async () => {
				const lunch_info_data = await getData('lunchInfo', '/get-lunch-info', credentials, true);
				setLunchInfo((lunch_info_data && typeof lunch_info_data === 'object') ? lunch_info_data : {});
			}));
		}

		if(requested_slices.has('scheduleInfo'))
		{
			refresh_tasks.push(queueRefresh('scheduleInfo', async () => {
				const schedule_info_data = await getData('scheduleInfo', '/get-schedule-info', {
					school_id,
					schedule_version_id,
					schedule_year_id,
					data_version_id,
					database:'edario'
				}, true);
				setScheduleInfo((schedule_info_data && typeof schedule_info_data === 'object') ? schedule_info_data : {});
			}));
		}

		if(requested_slices.has('scheduleStructureData'))
		{
			refresh_tasks.push(queueRefresh('scheduleStructureData', async () => {
				const schedule_structure_data = await getData('scheduleStructure', '/get-schedule-structure', {
					school_id,
					schedule_version_id,
					schedule_year_id,
				}, true);
				setScheduleStructureData((schedule_structure_data && typeof schedule_structure_data === 'object') ? schedule_structure_data : {});
			}));
		}

		await Promise.all(refresh_tasks);
	}, [
		schoolType,
		schoolInfo.school_id,
		schoolInfo.current_schedule_version_id,
		schoolInfo.current_data_version_id,
		schoolInfo.current_schedule_year_id,
		setClassrooms,
		setCourses,
		setDepartments,
		setLabels,
		setLunchInfo,
		setPulloutGroups,
		setScheduleInfo,
		setScheduleStructureData,
		setSections,
		setStudents,
		setSubsections,
		setTeachers,
	]);

	useEffect(() => {
		if(typeof window === 'undefined') return undefined;
		if(schoolInfoLoading || schoolType !== 'hsms' || !schoolInfo.school_id || !schoolInfo.current_schedule_version_id) return undefined;

		const handleHSMSDataInvalidated = (event) => {
			const invalidation_detail = event?.detail || {};
			if(`${invalidation_detail.school_id || ''}` !== `${schoolInfo.school_id || ''}`) return;
			if(`${invalidation_detail.schedule_version_id || ''}` !== `${schoolInfo.current_schedule_version_id || ''}`) return;

			const invalidated_slices = normalizeHSMSSlices(invalidation_detail.slices || []);
			const conflict_relevant_slices = new Set(['courses', 'teachers', 'classrooms', 'students', 'sections', 'subsections', 'scheduleInfo', 'scheduleStructureData', 'periods']);
			if(invalidated_slices.some((slice_name) => conflict_relevant_slices.has(slice_name))) {
				useMagnetboardStore.getState().invalidateConflictData();
			}

			refreshHSMSDataSlices({
				slices: invalidated_slices,
				targets: invalidation_detail.targets || null,
				passed_credentials: {
					school_id: schoolInfo.school_id,
					schedule_version_id: schoolInfo.current_schedule_version_id,
					data_version_id: schoolInfo.current_data_version_id,
					schedule_year_id: schoolInfo.current_schedule_year_id,
				}
			});
		};

		window.addEventListener(HSMS_SHARED_DATA_INVALIDATED_EVENT, handleHSMSDataInvalidated);
		return () => window.removeEventListener(HSMS_SHARED_DATA_INVALIDATED_EVENT, handleHSMSDataInvalidated);
	}, [
		schoolInfoLoading,
		schoolType,
		schoolInfo.school_id,
		schoolInfo.current_schedule_version_id,
		schoolInfo.current_data_version_id,
		schoolInfo.current_schedule_year_id,
		refreshHSMSDataSlices,
	]);
	
	const getSchoolData = (school_params = {}, _callback = () => {}, ) => {
		resetState();

		getData('schoolInfo', '/get-school-info', school_params, true).then(school_info_data => {
			const school_id = school_info_data.school_id;
			const current_schedule_version_id = school_info_data.current_schedule_version_id;
			const current_schedule_year_id = school_info_data.current_schedule_year_id;
			const current_data_version_id = school_info_data.current_data_version_id;

			const school_type = (school_info_data.type === '1') ? 'hsms' : ((school_info_data.type === '2') ? 'elementary' : null);
			const database = (school_type === 'hsms') ? 'edario' : 'elementary';
			const load_screen = ('params' in match && match.params.screen) ? match.params.screen : ((school_params.user_role_id === '4' || school_type === 'elementary') ? 'magnetboard' : 'progress');
			
			setCurrentSchoolID(school_id);
			setSchoolInfo(school_info_data);
			setLoadScreen(load_screen);
			setSchoolType(school_type);
			setSchoolInfoLoading(false);

			localStorage.setItem('currentSchoolID', school_id);
			localStorage.setItem('currentScheduleVersionID', school_info_data.current_schedule_version_id);
			localStorage.setItem('currentScheduleYearID', school_info_data.current_schedule_year_id);
			
			getNotifications({school_id: school_id, schedule_year_id:current_schedule_year_id}).then(notifications => {
				setNotifications(notifications);
			});
			
			const app_subscreen_promise = getData('appSubscreens', '/get-app-subscreens', {school_id: school_id, database:database}, true).then(app_subscreens => {
				setAppSubscreens(app_subscreens);
			});
			
			const school_progress_promise = getData('schoolProgress', '/get-school-progress', {school_id: school_id, schedule_version_id:current_schedule_version_id, schedule_year_id:current_schedule_year_id, database:database}, true).then(user_progress_data => {
				const role_pages = user_progress_data.role_pages;
				
				setRolePages(role_pages);
				setSchoolProgressLoaded(true);
			});
			
			const schedule_info_promise = getData('scheduleInfo', '/get-schedule-info', {school_id: school_id, schedule_version_id:current_schedule_version_id, schedule_year_id:current_schedule_year_id, data_version_id:current_data_version_id, database:database}, true).then(schedule_info_data => {
				const schedule_years = (Array.isArray(schedule_info_data?.schedule_years)) ? schedule_info_data.schedule_years : [];
				const current_schedule_year_info = schedule_years.find(schedule_year => `${schedule_year.schedule_year_id}` === `${current_schedule_year_id}`);
				const school_subscribed = (current_schedule_year_info) ? current_schedule_year_info.num_seats !== '0' : false;
				setSchoolSubscribed(school_subscribed);
				setScheduleInfo((schedule_info_data && typeof schedule_info_data === 'object') ? schedule_info_data : {});
				setScheduleInfoLoading(false);
			});
			
			const schedule_structure_promise = (database === 'edario') ? getData('scheduleStructure', '/get-schedule-structure', {school_id:school_id, schedule_version_id:current_schedule_version_id, schedule_year_id:current_schedule_year_id}, true).then(schedule_structure_data => {
				setScheduleStructureData(schedule_structure_data);
				setScheduleStructureDataLoading(false);
			}) : getData('periods', '/elementary/get-periods', {school_id:school_id, schedule_version_id:current_schedule_version_id, schedule_year_id:current_schedule_year_id}, true, 'elementary').then(periods => {
				setPeriods(periods);
				setPeriodsLoading(false);
			});
			
			const users_promise = getData('users', '/admin/get-users', {school_id:school_id, schedule_version_id:current_schedule_version_id}, true).then(users => {
				setUsers([...users]);
				setUsersLoading(false);
			});

			handlePullNewData({school_id:school_id, schedule_version_id:current_schedule_version_id, data_version_id:current_data_version_id, database:database});

			Promise.all([app_subscreen_promise, school_progress_promise, schedule_info_promise, schedule_structure_promise, users_promise]);

			_callback(school_info_data);
		});
	}

	const handleSwitchCurrentYear = (data) => {
		// data includes school_id, selected_schedule_year_id, new_year (bool)

		// If user is moving into a new year and this school has a previously subscribed year then let them select what data to roll over
		if(('new_year' in data) && data.new_year && schoolInfo.last_subscribed_data_version_id) 
		{
			setSelectedScheduleYearID(data.selected_schedule_year_id);
			setShowNewYearModal(true);
			return false;
		}

		setShowSwitchingYearModal(true);
		sessionStorage.clear();
		
		switchCurrentYear(data, () => {
			const school_params = {school_id: schoolInfo.school_id, user_role_id:userInfo.role_id, schedule_year_id:data.selected_schedule_year_id, schedule_version_id:null};
			getSchoolData(school_params, () => {
				const load_screen = (userInfo.user_role_id === '4') ? 'magnetboard' : 'progress';
				setLoadScreen(load_screen);
				setSelectedScheduleYearID(null);
				setShowSwitchingYearModal(false);
				history.push(`/app/${schoolType}/${load_screen}`);
			});
		});
	}

	const handleSwitchCurrentScheduleVersion = (data) => {
		// data includes school_id, selected_schedule_version_id, selected_schedule_year_id
		setShowSwitchingYearModal(true);
		sessionStorage.clear();

		const school_params = {
			school_id: schoolInfo.school_id,
			user_role_id: userInfo.role_id,
			schedule_year_id: data.selected_schedule_year_id,
			schedule_version_id: data.selected_schedule_version_id
		};

		getSchoolData(school_params, () => {
			const load_screen = (userInfo.user_role_id === '4') ? 'magnetboard' : 'progress';
			setLoadScreen(load_screen);
			setShowSwitchingYearModal(false);
			history.push(`/app/${schoolType}/${load_screen}`);
		});
	}

	const showScheduleVersionLimitModal = (max_schedules_allowed) => {
		swal({
			title: "Sorry",
			text: `Sorry you cannot have more than ${max_schedules_allowed} schedule versions in a single year. If you'd like to create a new version, please delete one of your existing ones first.`,
			icon: "warning"
		});
	}

	const showCurrentYearDuplicateOnlyModal = () => {
		swal({
			title: "Sorry",
			text: "You can only duplicate schedule versions in the current scheduling year.",
			icon: "warning"
		});
	}

	const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

	const findRecoveredDuplicateSchedule = (schedule_info_data, original_schedules, selected_schedule_year_id, source_schedule_version_id, duplicate_schedule_name) => {
		const schedules = Array.isArray(schedule_info_data?.schedules) ? schedule_info_data.schedules : [];
		const original_schedule_ids = new Set((Array.isArray(original_schedules) ? original_schedules : []).map(schedule => `${schedule.schedule_version_id}`));
		const matching_schedules = schedules.filter(schedule => (
			`${schedule.schedule_year_id}` === `${selected_schedule_year_id}` &&
			`${schedule.schedule_version_id}` !== `${source_schedule_version_id}` &&
			`${schedule.is_deleted}` !== '1' &&
			schedule.schedule_name === duplicate_schedule_name &&
			!original_schedule_ids.has(`${schedule.schedule_version_id}`)
		));

		if(matching_schedules.length === 0) return null;

		return matching_schedules.sort((schedule_a, schedule_b) => {
			const schedule_a_id = parseInt(schedule_a.schedule_version_id, 10) || 0;
			const schedule_b_id = parseInt(schedule_b.schedule_version_id, 10) || 0;
			return schedule_b_id - schedule_a_id;
		})[0];
	}

	const recoverDuplicateScheduleVersion = async ({data, selected_schedule_year_id, duplicate_schedule_name, original_schedules}) => {
		const recovery_delays = [3000, 5000, 7000, 10000, 10000, 10000, 10000, 15000, 15000];

		for(let attempt_index = 0; attempt_index < recovery_delays.length; attempt_index++)
		{
			await wait(recovery_delays[attempt_index]);

			const schedule_info_data = await getData('scheduleInfo', '/get-schedule-info', {
				school_id: schoolInfo.school_id,
				schedule_version_id: data.schedule_version_id,
				schedule_year_id: selected_schedule_year_id,
				data_version_id: schoolInfo.current_data_version_id,
				database:'edario'
			}, true);

			const recovered_schedule = findRecoveredDuplicateSchedule(
				schedule_info_data,
				original_schedules,
				selected_schedule_year_id,
				data.schedule_version_id,
				duplicate_schedule_name
			);

			if(recovered_schedule) return recovered_schedule;
		}

		return null;
	}

	const handleDuplicateScheduleVersion = async (data) => {
		// data includes school_id, schedule_version_id, schedule_year_id, schedule_name
		const selected_schedule_year_id = data.schedule_year_id || schoolInfo.current_schedule_year_id;
		const app_current_schedule_year_id = scheduleInfo.app_current_schedule_year_id;

		if(`${selected_schedule_year_id}` !== `${app_current_schedule_year_id}`)
		{
			showCurrentYearDuplicateOnlyModal();
			return {success:false, schedule_version_id:null};
		}

		const schedule_year_info = ('schedule_years' in scheduleInfo) ? scheduleInfo.schedule_years.find(schedule_year => `${schedule_year.schedule_year_id}` === `${selected_schedule_year_id}`) : null;
		const max_schedules_allowed = (schedule_year_info && schedule_year_info.num_schedules_allowed) ? parseInt(schedule_year_info.num_schedules_allowed, 10) : 5;
		const current_schedule_count = ('schedules' in scheduleInfo) ? scheduleInfo.schedules.filter(schedule => `${schedule.schedule_year_id}` === `${selected_schedule_year_id}` && `${schedule.is_deleted}` !== '1').length : 0;

		if(current_schedule_count >= max_schedules_allowed)
		{
			showScheduleVersionLimitModal(max_schedules_allowed);
			return {success:false, schedule_version_id:null};
		}

		setShowSwitchingYearModal(true);
		sessionStorage.clear();

		const duplicate_schedule_name = (data.schedule_name) ? `${data.schedule_name} Copy` : 'Schedule Copy';
		const original_schedules = ('schedules' in scheduleInfo && Array.isArray(scheduleInfo.schedules)) ? [...scheduleInfo.schedules] : [];
		const save_schedule_data = {
			school_id: schoolInfo.school_id,
			save_type: 'new',
			schedule_version_id: data.schedule_version_id,
			schedule_name: duplicate_schedule_name
		};

		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/save-schedule';
		const myInit = {
			response: true,
			body: JSON.stringify(save_schedule_data),
		};

		try {
			const response = await API.post(apiName, url, myInit);
			const new_schedule_version_id = response.data.new_schedule_version_id;

			const school_params = {
				school_id: schoolInfo.school_id,
				user_role_id: userInfo.role_id,
				schedule_year_id: data.schedule_year_id || schoolInfo.current_schedule_year_id,
				schedule_version_id: new_schedule_version_id
			};

			getSchoolData(school_params, () => {
				const load_screen = (userInfo.user_role_id === '4') ? 'magnetboard' : 'progress';
				setLoadScreen(load_screen);
				setShowSwitchingYearModal(false);
				history.push(`/app/${schoolType}/${load_screen}`);
			});

			return {success:true, schedule_version_id:new_schedule_version_id};
		} catch (e) {
			let handled_error = false;
			if(e && e.response && e.response.data && e.response.data.msg === 'Schedule versions can only be duplicated in current year')
			{
				setShowSwitchingYearModal(false);
				showCurrentYearDuplicateOnlyModal();
				handled_error = true;
			}
			if(e && e.response && e.response.data && e.response.data.msg === 'Schedule version limit reached')
			{
				setShowSwitchingYearModal(false);
				const backend_max_schedules_allowed = (e.response.data.num_schedules_allowed) ? parseInt(e.response.data.num_schedules_allowed, 10) : max_schedules_allowed;
				showScheduleVersionLimitModal(backend_max_schedules_allowed);
				handled_error = true;
			}
			if(e && e.response && e.response.data && e.response.data.msg === 'Failed to duplicate schedule version')
			{
				setShowSwitchingYearModal(false);
				swal({
					title: "Duplicate failed",
					text: "The schedule could not be duplicated. Please try again.",
					icon: "error"
				});
				handled_error = true;
			}
			if(!handled_error)
			{
				try {
					const recovered_schedule = await recoverDuplicateScheduleVersion({
						data,
						selected_schedule_year_id,
						duplicate_schedule_name,
						original_schedules
					});

					if(recovered_schedule)
					{
						const school_params = {
							school_id: schoolInfo.school_id,
							user_role_id: userInfo.role_id,
							schedule_year_id: selected_schedule_year_id,
							schedule_version_id: recovered_schedule.schedule_version_id
						};

						getSchoolData(school_params, () => {
							const load_screen = (userInfo.user_role_id === '4') ? 'magnetboard' : 'progress';
							setLoadScreen(load_screen);
							setShowSwitchingYearModal(false);
							history.push(`/app/${schoolType}/${load_screen}`);
						});

						return {success:true, schedule_version_id:recovered_schedule.schedule_version_id, recovered:true};
					}
				} catch (recovery_error) {
					console.log(recovery_error);
				}

				setShowSwitchingYearModal(false);
				swal({
					title: "Duplicate still processing",
					text: "The schedule copy is taking longer than expected. It may still finish in the background, so please refresh in a minute before trying again.",
					icon: "warning"
				});
			}
			return {success:false, schedule_version_id:null};
		}
	}

	const handleDeleteScheduleVersion = async (data) => {
		setShowSwitchingYearModal(true);
		sessionStorage.clear();

		const save_schedule_data = {
			school_id: schoolInfo.school_id,
			save_type: 'delete',
			schedule_version_id: data.schedule_version_id
		};

		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/save-schedule';
		const myInit = {
			response: true,
			body: JSON.stringify(save_schedule_data),
		};

		try {
			const response = await API.post(apiName, url, myInit);
			const new_schedule_version_id = response.data.new_schedule_version_id;
			const next_load_screen = loadScreen || ((userInfo.user_role_id === '4') ? 'magnetboard' : 'progress');

			const school_params = {
				school_id: schoolInfo.school_id,
				user_role_id: userInfo.role_id,
				schedule_year_id: data.schedule_year_id || schoolInfo.current_schedule_year_id,
				schedule_version_id: new_schedule_version_id
			};

			getSchoolData(school_params, () => {
				setLoadScreen(next_load_screen);
				setShowSwitchingYearModal(false);
				history.push(`/app/${schoolType}/${next_load_screen}`);
			});

			return {success:true, schedule_version_id:new_schedule_version_id};
		} catch (e) {
			setShowSwitchingYearModal(false);
			swal({
				title: "Delete failed",
				text: "The schedule could not be deleted. Please try again or contact support if the problem continues.",
				icon: "error"
			});
			return {success:false, schedule_version_id:null};
		}
	}

	const handleUpdateScheduleVersionName = async (data) => {
		// data includes schedule_version_id and schedule_name
		const new_schedule_name = (data.schedule_name || '').trim();
		if(!new_schedule_name) return {success:false, schedule_name:null};

		const save_schedule_data = {
			school_id: schoolInfo.school_id,
			save_type: 'schedule_name',
			schedule_version_id: data.schedule_version_id,
			schedule_name: new_schedule_name
		};

		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/save-schedule';
		const myInit = {
			response: true,
			body: JSON.stringify(save_schedule_data),
		};

		try {
			const response = await API.post(apiName, url, myInit);
			const updated_schedule_name = (response && response.data && response.data.new_schedule_name) ? response.data.new_schedule_name : new_schedule_name;

			setScheduleInfo(previous_schedule_info => {
				if(isEmpty(previous_schedule_info) || !('schedules' in previous_schedule_info)) return previous_schedule_info;

				const updated_schedules = previous_schedule_info.schedules.map(schedule => {
					if(`${schedule.schedule_version_id}` !== `${data.schedule_version_id}`) return schedule;
					return {
						...schedule,
						schedule_name: updated_schedule_name
					};
				});

				return {
					...previous_schedule_info,
					schedules: updated_schedules
				};
			});

			return {success:true, schedule_name:updated_schedule_name};
		} catch (e) {
			return {success:false, schedule_name:null};
		}
	}

	const closeUniversalSidebar = () => {
		setUniversalSidebarClosing(true);
		setShowUniversalSidebar(false);

		setTimeout(function () {
			setUniversalSidebarClosing(false);
		}, 500);
	}
	
	////////////////////////////////
	///// USE EFFECT FUNCTIONS /////
	////////////////////////////////
	// Calculate student match percentages
	useEffect(() => {
		if(sectionsLoading || studentsLoading || coursesLoading || lunchInfoLoading || isEmpty(lunchInfo) || isEmpty(scheduleStructureData) || schoolType !== 'hsms') return;
		
		// Calculate student matches
		let student_match_percentages = {};
		students.forEach((student_info, student_index) => {
			const student_id = student_info.student_id;
			const student_requests = student_info.student_requests;
			const student_sections = sections.filter(section => ('course_periods' in section) && section.course_periods.length > 0 && ('student_list' in section) && section.student_list.includes(student_id));

			const matched_student_requests = calculateStudentRequestStats(student_requests, student_id, sections, courses, scheduleInfo);
			const num_student_requests = matched_student_requests.total_requests;

			// Calculate percent schedule full
			const schedule_fullness_info = calculateStudentScheduleFullness({
				scheduleInfo,
				scheduleStructureData,
				lunchInfo,
				studentSections: student_sections,
				courses,
			});

			const { student_match_percent, student_percent_schedule_full } = calculateStudentMatchPercent(num_student_requests, matched_student_requests.total_credits, matched_student_requests.matched_requests, schedule_fullness_info.required_units, schedule_fullness_info.matched_units)

			student_match_percentages[student_id] = {match_percent:student_match_percent, percent_schedule_full:student_percent_schedule_full};
		});

		setStudentMatchPercentages(student_match_percentages);
	}, [sectionsLoading, studentsLoading, coursesLoading, lunchInfoLoading, students, sections, scheduleInfo, scheduleStructureData, lunchInfo, schoolType, courses]);

	useEffect(() => {
		if(!universalSidebarLoadingInfo) setShowUniversalSidebar(false);
		if(!showUniversalSidebar) setUniversalSidebarClosing(false);
	}, [loadScreen, universalSidebarLoadingInfo, showUniversalSidebar]);

	useEffect(() => {
		if(isAuth && currentUser)
		{
			let user_email = ('attributes' in currentUser && 'email' in currentUser.attributes)  ? currentUser.attributes.email : (('challengeParam' in currentUser && 'userAttributes' in currentUser.challengeParam && 'email' in currentUser.challengeParam.userAttributes) ? currentUser.challengeParam.userAttributes.email : null);
			let user_params = {school_id:null, user_email:user_email};

			getData('userInfo', '/get-user-info', user_params, true).then(user_info_data => {
				setUserInfo(user_info_data);
				setUserInfoLoading(false);
			
				let school_id = getJSONFromStorage('currentSchoolID', false);
				const user_schools = getUniqueSchoolsById(Array.isArray(user_info_data?.schools) ? user_info_data.schools : []);
				const active_user_schools = user_schools.filter(schoolConnectionIsActive);
				const stored_school_is_active = school_id && active_user_schools.some(school_info => `${school_info.school_id}` === `${school_id}`);

				if(school_id && !stored_school_is_active)
				{
					school_id = null;
					localStorage.setItem('currentSchoolID', null);
					localStorage.setItem('currentScheduleVersionID', null);
					localStorage.setItem('currentScheduleYearID', null);
					setCurrentSchoolID(null);
					setSchoolInfo({});
				}
				
				if(!school_id)
				{
					if(active_user_schools.length === 1) school_id = active_user_schools[0].school_id;
				}
				
				if(!school_id)
				{
					setSchoolInfoLoading(false);
					setLoadScreen('select_school')
					history.push(`/app/select_school`);
					return false;
				}

				const current_schedule_year_id = JSONexists('currentScheduleYearID') ? localStorage.getItem('currentScheduleYearID') : null;
				const current_schedule_version_id = JSONexists('currentScheduleVersionID') ? localStorage.getItem('currentScheduleVersionID') : null;
				const school_params = {school_id:school_id, user_role_id:user_info_data.role_id, schedule_year_id:current_schedule_year_id, schedule_version_id: current_schedule_version_id};
				getSchoolData(school_params);
			});
		}
		else
		{
			setLoadScreen('login');
		}
	}, [isAuth, currentUser, history, getUniqueSchoolsById]);
	
	useEffect(() => {
		authUser();
	}, []);

	const screen_style = (isAuth) ? {gridTemplateColumns:'126px 1fr'} : {gridTemplateColumns:'1fr'};
	const user_is_admin = (userInfo) ? (userInfo.role_id === '1' || userInfo.role_id === '2') : false;
	const navigation_guard_info = {
		authComplete,
		isAuth,
		userInfo,
		userInfoLoading,
		schoolType,
		schoolInfoLoading,
		rolePages,
	};
	const assignedSupervisorDepartmentCount = useMemo(() => {
		if(`${userInfo?.role_id || ''}` !== '3' || !Array.isArray(departments)) return 0;

		const user_id = `${userInfo?.user_id || ''}`;
		if(!user_id) return 0;

		return departments.reduce((total, department_info) => {
			const supervisors = Array.isArray(department_info?.supervisors) ? department_info.supervisors : [];
			const user_is_assigned = supervisors.some(supervisor_info => `${supervisor_info?.user_id || ''}` === user_id);
			return user_is_assigned ? total + 1 : total;
		}, 0);
	}, [departments, userInfo]);
	const showSupervisorNoDepartmentsModal = (
		isAuth &&
		currentSchoolID &&
		schoolType === 'hsms' &&
		!userInfoLoading &&
		!schoolInfoLoading &&
		!departmentsLoading &&
		`${userInfo?.role_id || ''}` === '3' &&
		assignedSupervisorDepartmentCount === 0
	);
	const supervisorDepartmentAccessPending = (
		isAuth &&
		!userInfoLoading &&
		`${userInfo?.role_id || ''}` === '3' &&
		loadScreen !== 'select_school' &&
		(
			!currentSchoolID ||
			schoolInfoLoading ||
			!schoolType ||
			(schoolType === 'hsms' && departmentsLoading)
		)
	);
	const appAccessPreflightPending = isAuth && authComplete && userInfoLoading;
	const handleMagnetboardSidebarClick = useCallback(() => {
		logMagnetboardDiagnostic('sidebar-click', {from_screen: loadScreen});
		setLoadScreen('magnetboard');
	}, [loadScreen, setLoadScreen]);

	//console.log({schoolType, loadScreen});
	//console.log({showSwitchingYearModal});
	
	return (
		<BrowserRouter>
			<div className="App">
				{isUnderMaintenance.current ?
					(
						<div className='modal-screen'>
							<div className='modal-screen-content'>
								<Link className="under-maintenance-logo-container" to={`/`}>
									<img className='under-maintenance-logo-img' src={require("../images/logo-dark.png")} alt='Edario Logo' />
									<div className='under-maintenance-logo-text'>edario</div>
								</Link>
								<div className='under-maintenance-screen'>
									<div>
										<h1>Site Under Maintenance</h1>
										<p>We are currently working on making site upgrades. Please check back soon!</p>
									</div>
									<img src={require('../images/maintenance.png')} alt='Under Maintenance'/>
								</div>
							</div>
						</div>
					)
					:(showSwitchingYearModal) ?
					(
						<SwitchingYearsModal/>
					):
					(
						<>
						<UniversalSidebar match={match} schoolInfo={schoolInfo} scheduleInfo={scheduleInfo} users={users} userInfo={userInfo} sections={sections} courses={courses} students={students} teachers={teachers} classrooms={classrooms} showUniversalSidebar={showUniversalSidebar} setShowUniversalSidebar={setShowUniversalSidebar} universalSidebarClosing={universalSidebarClosing} setUniversalSidebarClosing={setUniversalSidebarClosing} universalSidebarLoadingInfo={universalSidebarLoadingInfo} setUniversalSidebarLoadingInfo={setUniversalSidebarLoadingInfo} closeUniversalSidebar={closeUniversalSidebar}
						screenAllowsUniversalSidebar={screenAllowsUniversalSidebar}
						isAuth={isAuth}/>
						{(appAccessPreflightPending || supervisorDepartmentAccessPending) &&
							<SupervisorDepartmentAccessLoading/>
						}
						{showSupervisorNoDepartmentsModal &&
							<SupervisorNoDepartmentsModal/>
						}
						<div className='main-page-with-sidebar-container'>
							<div className='main-page-with-sidebar' style={screen_style}>
								{showNewYearModal &&
									<NewYearModal selectedScheduleYearID={selectedScheduleYearID} schoolInfo={schoolInfo} scheduleInfo={scheduleInfo} closeModal={() => setShowNewYearModal(false)} callBack={handleSwitchCurrentYear} scheduleStructureData={scheduleStructureData} departments={departments} teachers={teachers} courses={courses} classrooms={classrooms} sections={sections} users={users} userInfo={userInfo} />
								}
								{isAuth &&
									<div className='main-page-with-sidebar-sidebar'>
										<div className='main-sidebar-logo-outer-container'>
											<Link className="main-sidebar-logo-container" to={`/app/`}>
												<img className='main-sidebar-logo-img' src={require("../images/logo-white.png")} alt='Edario Logo' />
												<div className='main-sidebar-logo-text'>edario</div>
											</Link>
										</div>
										{currentSchoolID &&
											<>
											{(!userInfoLoading && ((schoolType === 'hsms' && userInfo.role_id !== '4') || (schoolType === 'elementary' && user_is_admin))) &&
												<Link className={`main-sidebar-btn ${loadScreen === 'progress' && 'main-sidebar-btn-selected'}`} to={`/app/${schoolType}/progress`} onClick={() => setLoadScreen('progress')}>
													<FontAwesomeIcon className='main-sidebar-btn-icon' icon={faWandMagicSparkles}/>
													<div>Scheduling Wizard</div>
												</Link>
											}
											<Link className={`main-sidebar-btn ${loadScreen === 'school_data' && 'main-sidebar-btn-selected'}`} to={`/app/${schoolType}/school_data`} onClick={() => setLoadScreen('school_data')}>
												<FontAwesomeIcon className='main-sidebar-btn-icon' icon={faIdCard}/>
												<div>School Data</div>
											</Link>
											{(schoolType === 'hsms') &&
												<Link className={`main-sidebar-btn ${loadScreen === 'scheduling_data' && 'main-sidebar-btn-selected'}`} to={`/app/${schoolType}/scheduling_data`} onClick={() => setLoadScreen('scheduling_data')}>
													<FontAwesomeIcon className='main-sidebar-btn-icon' icon={faNetworkWired}/>
													<div>Scheduling Data</div>
												</Link>
											}
											{(!userInfoLoading) &&
												<Link className={`main-sidebar-btn ${loadScreen === 'magnetboard' && 'main-sidebar-btn-selected'}`} to={`/app/${schoolType}/magnetboard`} onClick={handleMagnetboardSidebarClick}>
													<FontAwesomeIcon className='main-sidebar-btn-icon' icon={faCalendarDays}/>
													<div>Magnetboard</div>
												</Link>
											}
											</>
										}
										<Link id='main-sidebar-settings-btn' className={`main-sidebar-btn ${loadScreen === 'settings' && 'main-sidebar-btn-selected'}`} to={`/app/${schoolType}/settings/account/`} onClick={() => setLoadScreen('settings')}>
											<FontAwesomeIcon className='main-sidebar-btn-icon' icon={faCog}/>
											<div>Settings</div>
										</Link>
										<Link id='main-sidebar-documentation-btn' className={`main-sidebar-btn ${loadScreen === 'documentation' && 'main-sidebar-btn-selected'}`} to={`/app/${schoolType}/documentation/`} onClick={() => setLoadScreen('documentation')}>
											<FontAwesomeIcon className='main-sidebar-btn-icon' icon={faQuestion}/>
											<div>Help</div>
										</Link>
										
									</div>
								}
								<div className='main-page-with-sidebar-content'>
									{isAuth &&
										<>
											<Navbar schoolType={schoolType} loadScreen={loadScreen} setLoadScreen={setLoadScreen} schoolInfo={schoolInfo} schoolInfoLoading={schoolInfoLoading} userInfo={userInfo} userInfoLoading={userInfoLoading} scheduleInfo={scheduleInfo} scheduleInfoLoading={scheduleInfoLoading} notifications={notifications} setNotifications={setNotifications} appSubscreens={appSubscreens} handleSwitchCurrentYear={handleSwitchCurrentYear} handleSwitchCurrentScheduleVersion={handleSwitchCurrentScheduleVersion} handleDuplicateScheduleVersion={handleDuplicateScheduleVersion} handleDeleteScheduleVersion={handleDeleteScheduleVersion} handleUpdateScheduleVersionName={handleUpdateScheduleVersionName} handleLogout={() => handleLogout()}/>
										</>
									}
								<div className='main-page-with-sidebar-content-inner'>
									<div id='main-content'>
										{isAuth && schoolType === 'hsms' && (
											<HSMSReportsHost
												schoolSubscribed={schoolSubscribed}
												schoolInfoLoading={schoolInfoLoading}
												schoolInfo={schoolInfo}
												scheduleInfoLoading={scheduleInfoLoading}
												scheduleInfo={scheduleInfo}
												scheduleStructureDataLoading={scheduleStructureDataLoading}
												scheduleStructureData={scheduleStructureData}
												sectionsLoading={sectionsLoading}
												teachersLoading={teachersLoading}
												coursesLoading={coursesLoading}
												classroomsLoading={classroomsLoading}
												studentsLoading={studentsLoading}
												userInfo={userInfo}
												sections={sections}
												subsections={subsections}
												setSections={setSections}
												setLabels={setLabels}
												setSubsections={setSubsections}
												students={students}
												setStudents={setStudents}
												teachers={teachers}
												setTeachers={setTeachers}
												courses={courses}
												setCourses={setCourses}
												classrooms={classrooms}
												setClassrooms={setClassrooms}
												departments={departments}
												labels={labels}
												lunchInfo={lunchInfo}
											/>
										)}
											<Switch>
												<Route exact path='/app/' render={() => ( <LoadPage isAuth={isAuth} authComplete={authComplete} schoolType={schoolType} loadScreen={loadScreen} />)}/>
												<Route path='/app/login' render={() => (<LogIn isAuth={isAuth} authComplete={authComplete} username={username} newPassword={newPassword} onUsernameChange={handleUsernameChange} onPasswordChange={handlePasswordChange} onNewPasswordChange={handleNewPasswordChange} onVerificationCodeChange={handleVerificationCodeChange} handleLogin={handleLogin} handleCompleteNewPassword={handleCompleteNewPassword} handleForgotPassword={handleForgotPassword} handleForgotPasswordSubmit={handleForgotPasswordSubmit} setScreenAllowsUniversalSidebar={setScreenAllowsUniversalSidebar} setUniversalSidebarLoadingInfo={setUniversalSidebarLoadingInfo} />)} />
												<ProtectedRoute guardInfo={navigation_guard_info} routeType='settings' path='/app/:school_type/settings/:page?' render={(props) => (<Settings isAuth={isAuth} authComplete={authComplete} currentSchoolID={currentSchoolID} schoolInfo={schoolInfo} schoolInfoLoading={schoolInfoLoading} setSchoolInfo={setSchoolInfo} userInfo={userInfo} userInfoLoading={userInfoLoading} setUserInfo={setUserInfo}  users={users} usersLoading={usersLoading} setUsers={setUsers} schoolType={schoolType} setLoadScreen={setLoadScreen} setScreenAllowsUniversalSidebar={setScreenAllowsUniversalSidebar}setUniversalSidebarLoadingInfo={setUniversalSidebarLoadingInfo} {...props}/>)} />
												<ProtectedRoute guardInfo={navigation_guard_info} routeType='documentation' path='/app/:school_type/documentation/:screen?/:subscreen?/:topic?/:sis?/' render={(props) => (<Documentation isAuth={isAuth} authComplete={authComplete} schoolInfo={schoolInfo} schoolInfoLoading={schoolInfoLoading} userInfo={userInfo} schoolType={schoolType} setLoadScreen={setLoadScreen} setScreenAllowsUniversalSidebar={setScreenAllowsUniversalSidebar} setUniversalSidebarLoadingInfo={setUniversalSidebarLoadingInfo} {...props}/>)}  />
												<Route path='/app/select_school' render={() => ( <SelectSchool isAuth={isAuth} authComplete={authComplete} schoolType={schoolType} setLoadScreen={setLoadScreen} userInfo={userInfo} userInfoLoading={userInfoLoading} setUserInfo={setUserInfo} setCurrentSchoolID={setCurrentSchoolID} schoolInfo={schoolInfo} schoolInfoLoading={schoolInfoLoading} setSchoolInfo={setSchoolInfo} resetState={resetState} getSchoolData={getSchoolData} setScreenAllowsUniversalSidebar={setScreenAllowsUniversalSidebar} setUniversalSidebarLoadingInfo={setUniversalSidebarLoadingInfo}/>)}/>
												<ProtectedRoute guardInfo={navigation_guard_info} routeType='progress' path='/app/:school_type/progress/:screen?/:subscreen?/' render={(props) => ( <Progress isAuth={isAuth} authComplete={authComplete} setShowSwitchingYearModal={setShowSwitchingYearModal} schoolSubscribed={schoolSubscribed} schoolProgressLoaded={schoolProgressLoaded} schoolInfo={schoolInfo} schoolInfoLoading={schoolInfoLoading} setSchoolInfoLoading={setSchoolInfoLoading} setSchoolInfo={setSchoolInfo} schoolType={schoolType} scheduleInfo={scheduleInfo} setScheduleInfo={setScheduleInfo} scheduleInfoLoading={scheduleInfoLoading} scheduleStructureData={scheduleStructureData} setScheduleStructureData={setScheduleStructureData} scheduleStructureDataLoading={scheduleStructureDataLoading} periods={periods} setPeriods={setPeriods} periodsLoading={periodsLoading} rolePages={rolePages} setRolePages={setRolePages} userInfo={userInfo} users={users} usersLoading={usersLoading} appSubscreens={appSubscreens} setNotifications={setNotifications} loadScreen={loadScreen} setLoadScreen={setLoadScreen} departmentsLoading={departmentsLoading} departments={departments} setDepartments={setDepartments} teachersLoading={teachersLoading} setTeachersLoading={setTeachersLoading} teachers={teachers} setTeachers={setTeachers} coursesLoading={coursesLoading} setCoursesLoading={setCoursesLoading} courses={courses} setCourses={setCourses} classroomsLoading={classroomsLoading} setClassroomsLoading={setClassroomsLoading} classrooms={classrooms} setClassrooms={setClassrooms} studentsLoading={studentsLoading} setStudentsLoading={setStudentsLoading} students={students} setStudents={setStudents} studentMatchPercentages={studentMatchPercentages} blocksLoading={blocksLoading} blocks={blocks} setBlocks={setBlocks} pulloutGroups={pulloutGroups} setPulloutGroups={setPulloutGroups} homeroomGradeGroups={homeroomGradeGroups} homeroomGradeGroupsLoading={homeroomGradeGroupsLoading} setHomeroomGradeGroups={setHomeroomGradeGroups} sectionsLoading={sectionsLoading} setSectionsLoading={setSectionsLoading} sections={sections} setSections={setSections} subsections={subsections} setSubsections={setSubsections} lunchInfo={lunchInfo} setLunchInfo={setLunchInfo} labels={labels} setLabels={setLabels} setScreenAllowsUniversalSidebar={setScreenAllowsUniversalSidebar} setUniversalSidebarLoadingInfo={setUniversalSidebarLoadingInfo} handlePullNewData={handlePullNewData} {...props}/>)}/>
												<ProtectedRoute guardInfo={navigation_guard_info} routeType='school_data' path='/app/:school_type/school_data/:screen?/' render={(props) => ( <SchoolData isAuth={isAuth} authComplete={authComplete} schoolSubscribed={schoolSubscribed} schoolProgressLoaded={schoolProgressLoaded} schoolInfo={schoolInfo} schoolInfoLoading={schoolInfoLoading} setSchoolInfo={setSchoolInfo} schoolType={schoolType} scheduleInfo={scheduleInfo} setScheduleInfo={setScheduleInfo} scheduleInfoLoading={scheduleInfoLoading} scheduleStructureData={scheduleStructureData} setScheduleStructureData={setScheduleStructureData} scheduleStructureDataLoading={scheduleStructureDataLoading} periods={periods} setPeriods={setPeriods} periodsLoading={periodsLoading} rolePages={rolePages} setRolePages={setRolePages} userInfo={userInfo} users={users} usersLoading={usersLoading} appSubscreens={appSubscreens} setNotifications={setNotifications} setLoadScreen={setLoadScreen} departmentsLoading={departmentsLoading} departments={departments} setDepartments={setDepartments} teachersLoading={teachersLoading} teachers={teachers} setTeachers={setTeachers} coursesLoading={coursesLoading} courses={courses} setCourses={setCourses} classroomsLoading={classroomsLoading} classrooms={classrooms} setClassrooms={setClassrooms} studentsLoading={studentsLoading} students={students} setStudents={setStudents} blocks={blocks} pulloutGroups={pulloutGroups} setPulloutGroups={setPulloutGroups} sectionsLoading={sectionsLoading} sections={sections} setSections={setSections} subsections={subsections} setSubsections={setSubsections} lunchInfo={lunchInfo} setLunchInfo={setLunchInfo} labels={labels} setLabels={setLabels} handlePullNewData={handlePullNewData} setScreenAllowsUniversalSidebar={setScreenAllowsUniversalSidebar} setUniversalSidebarLoadingInfo={setUniversalSidebarLoadingInfo} {...props}/>)}/>
												<ProtectedRoute guardInfo={navigation_guard_info} routeType='scheduling_data' path='/app/:school_type/scheduling_data/:screen?/' render={(props) => ( <SchedulingData isAuth={isAuth} authComplete={authComplete} schoolProgressLoaded={schoolProgressLoaded} schoolInfo={schoolInfo} schoolInfoLoading={schoolInfoLoading} setSchoolInfo={setSchoolInfo} schoolType={schoolType} scheduleInfo={scheduleInfo} setScheduleInfo={setScheduleInfo} scheduleInfoLoading={scheduleInfoLoading} scheduleStructureData={scheduleStructureData} setScheduleStructureData={setScheduleStructureData} scheduleStructureDataLoading={scheduleStructureDataLoading} periods={periods} setPeriods={setPeriods} periodsLoading={periodsLoading} rolePages={rolePages} setRolePages={setRolePages} userInfo={userInfo} users={users} usersLoading={usersLoading} appSubscreens={appSubscreens} setNotifications={setNotifications} setLoadScreen={setLoadScreen} departmentsLoading={departmentsLoading} departments={departments} setDepartments={setDepartments} teachersLoading={teachersLoading} teachers={teachers} setTeachers={setTeachers} coursesLoading={coursesLoading} courses={courses} setCourses={setCourses} classroomsLoading={classroomsLoading} classrooms={classrooms} setClassrooms={setClassrooms} studentsLoading={studentsLoading} students={students} setStudents={setStudents} blocks={blocks} pulloutGroups={pulloutGroups} setPulloutGroups={setPulloutGroups} sectionsLoading={sectionsLoading} sections={sections} setSections={setSections} subsections={subsections} setSubsections={setSubsections} lunchInfo={lunchInfo} setLunchInfo={setLunchInfo} labels={labels} setLabels={setLabels} handlePullNewData={handlePullNewData} setScreenAllowsUniversalSidebar={setScreenAllowsUniversalSidebar} setUniversalSidebarLoadingInfo={setUniversalSidebarLoadingInfo} {...props}/>)}/>
												<ProtectedRoute guardInfo={navigation_guard_info} routeType='magnetboard' path='/app/:school_type/magnetboard' render={(props) => ( <Magnetboard isAuth={isAuth} authComplete={authComplete} schoolSubscribed={schoolSubscribed} schoolProgressLoaded={schoolProgressLoaded} schoolInfo={schoolInfo} schoolInfoLoading={schoolInfoLoading} scheduleInfo={scheduleInfo} setScheduleInfo={setScheduleInfo} scheduleStructureData={scheduleStructureData} setScheduleStructureData={setScheduleStructureData} scheduleInfoLoading={scheduleInfoLoading} scheduleStructureDataLoading={scheduleStructureDataLoading} periods={periods} setPeriods={setPeriods} periodsLoading={periodsLoading} userInfo={userInfo} appSubscreens={appSubscreens} setNotifications={setNotifications} departmentsLoading={departmentsLoading} departments={departments} setDepartments={setDepartments} teachersLoading={teachersLoading} teachers={teachers} setTeachers={setTeachers} coursesLoading={coursesLoading} courses={courses} setCourses={setCourses} classroomsLoading={classroomsLoading} classrooms={classrooms} setClassrooms={setClassrooms} studentsLoading={studentsLoading} students={students} setStudents={setStudents} studentMatchPercentages={studentMatchPercentages} blocksLoading={blocksLoading} blocks={blocks} setBlocks={setBlocks} sectionsLoading={sectionsLoading} sections={sections} setSections={setSections} subsections={subsections} setSubsections={setSubsections} lunchInfo={lunchInfo} setLunchInfo={setLunchInfo} labels={labels} setLabels={setLabels} loadScreen={loadScreen} setLoadScreen={setLoadScreen} setScreenAllowsUniversalSidebar={setScreenAllowsUniversalSidebar} setUniversalSidebarLoadingInfo={setUniversalSidebarLoadingInfo} handleDuplicateScheduleVersion={handleDuplicateScheduleVersion} handleDeleteScheduleVersion={handleDeleteScheduleVersion} handleUpdateScheduleVersionName={handleUpdateScheduleVersionName} {...props}/>)} />
											</Switch>
										</div>
									</div>
								</div>
							</div>
						</div>
						</>
					)
				}
			</div>
		</BrowserRouter>
	)
}
